/**
 * Betriebsart der Instanz — das Standalone-Signal aus dem SaaS-Vertrag
 * (Post #4701, Cloud `3ee8a41`): `GET /api/version → mode, peer_count`.
 *
 * Die Instanz sagt SELBST, was sie ist, und zwar abgeleitet aus ihrem
 * Verhalten (effektive Peer-Karte), nicht aus einer Umgebungsvariable. Das FE
 * leitet daraus die Sicht ab: Eine Kundeninstanz ohne Föderation zeigt keine
 * Server-Filter, keine Knoten-Spalten, kein Föderations-Vokabular.
 *
 * FEHLEND-TOLERANT mit Absicht: Beim Anschluss lieferte nur mac das Feld;
 * arkserver lief noch auf `19d5fd6` ohne `mode` (gemessen 15.08.). Ein
 * fehlendes Feld heißt „ältere cloud-api" — und jede ältere cloud-api ist
 * eine Föderations-Instanz, denn das Feld kam mit dem Standalone-Vertrag.
 * Der Rückfall `federation` ist also keine Vermutung, sondern die einzig
 * mögliche Wahrheit für den Bestand. Ebenso bei Netzfehlern: lieber die
 * volle Oberfläche zeigen als einem Föderations-Betreiber Filter wegnehmen.
 */
import { useEffect, useState } from 'react';
import { applyInstanceServices, type ServiceName } from '../lib/services';

export type InstanceMode = 'federation' | 'standalone';

/**
 * Fähigkeiten, die die Instanz in `/api/version.capabilities` meldet.
 * FEHLT DAS FELD, gilt die Fähigkeit als NICHT vorhanden — anders als bei den
 * Dienst-Ursprüngen, wo ein fehlender Schlüssel den Bestand meint. Grund:
 * Fähigkeiten beschreiben NEUE Endpunkte; „nicht gemeldet" heißt „gibt es
 * hier nicht", und der Client fragt gar nicht erst (Alex, 18.08.: wiederholte
 * 404 für /api/tenant/usage, das erst mit Gap 1 aus #4713 gebaut wird).
 */
let capabilities: Record<string, boolean> = {};

export function instanceHas(cap: string): boolean {
  return capabilities[cap] === true;
}

/**
 * Name der Instanz, wie sie sich selbst nennt (`/api/version.node`) — bei einer
 * Kundeninstanz der Tenant („david"), in der Föderation der Knoten
 * („arkserver"). Wird für die Protokoll-Zuordnung gebraucht: Alle Instanzen,
 * auch die von Kunden, schreiben in DAS EINE zentrale log-api (Alex, 18.08.:
 * „ich möchte nicht ein Log-API für jeden Tenant eigens ausrollen") — dann muss
 * am Protokoll stehen, WOHER eine Zeile kommt, sonst ist die tenant-spezifische
 * Abfrage im Fehlerfall nicht möglich.
 *
 * `null`, solange die Instanz nicht geantwortet hat. Kein Rückfall auf einen
 * geratenen Namen: Ein falscher Tenant am Protokolleintrag ist schlimmer als
 * gar keiner, weil er die Abfrage in die Irre führt.
 */
let nodeName: string | null = null;

export function instanceNode(): string | null {
  return nodeName;
}

let cached: InstanceMode | null = null;
let inFlight: Promise<InstanceMode> | null = null;
const listeners = new Set<(mode: InstanceMode) => void>();

/** Exportiert für die Messung gegen eine ECHTE Instanz (Probe 15.08. gegen
 *  cv2-probe:8078) und für Tests — der Hook selbst bleibt der einzige
 *  Konsument im Produktionspfad. */
export async function fetchMode(): Promise<InstanceMode> {
  try {
    const res = await fetch('/api/version');
    if (!res.ok) return 'federation';
    const body = await res.json() as { mode?: unknown; node?: unknown; services?: unknown; capabilities?: unknown };
    if (typeof body.node === 'string' && body.node) nodeName = body.node;
    if (body.capabilities && typeof body.capabilities === 'object') {
      capabilities = body.capabilities as Record<string, boolean>;
    }
    // EIN Abruf, beide Auskünfte (Vertrag 77eb0c9): die services-Map wandert
    // in die Dienst-Registry. Fehlendes Feld = ältere cloud-api = Bestand →
    // Registry nicht anfassen, die Betreiber-Defaults gelten vollständig.
    if (body.services && typeof body.services === 'object') {
      applyInstanceServices(body.services as Partial<Record<ServiceName, string | null>>);
    }
    return body.mode === 'standalone' ? 'standalone' : 'federation';
  } catch {
    return 'federation';
  }
}

function ensureLoaded(): Promise<InstanceMode> {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;
  inFlight = fetchMode().then((mode) => {
    cached = mode;
    inFlight = null;
    listeners.forEach((l) => l(mode));
    return mode;
  });
  return inFlight;
}

/**
 * BOOT-BARRIERE (Cloud-Befund 17.08., Davids Instanz): Service-Calls, die vor
 * dem Laden der Instanz-Map feuern (prefs-Pull, Auth-Refresh, Issue-Poll),
 * fielen auf FEDERATION_DEFAULTS zurück — der ERSTE Login ging gegen die
 * BETREIBER-auth statt gegen die Tenant-auth, ein Betreiber-Konto war damit
 * auf einer Kundeninstanz gültig. Das ist die Tenant-Grenze selbst, kein
 * Anzeige-Thema. Wer einen Dienst-Ursprung braucht, awaitet zuerst hier;
 * Fehler/404 lösen sofort auf (Bestand = Föderation), niemand hängt.
 */
export function instanceReady(): Promise<InstanceMode> {
  return ensureLoaded();
}

/** Reaktive Betriebsart. Startwert `federation`, bis die Instanz geantwortet
 *  hat — für den gesamten Bestand ist das ohnehin der Endwert. */
export function useInstanceMode(): InstanceMode {
  const [mode, setMode] = useState<InstanceMode>(cached ?? 'federation');
  useEffect(() => {
    if (cached) { setMode(cached); return; }
    listeners.add(setMode);
    ensureLoaded();
    return () => { listeners.delete(setMode); };
  }, []);
  return mode;
}
